import { FlexLayout, SaltIcons } from "@/design-system/salt";
import { IconActionButton } from "@/components/ui/icon-action-button";
import { usePageTitleOverride } from "@/components/dashboard/page-title-override";

interface PageHeaderProps {
  /**
   * The page's title. PUBLISHED TO THE CHROME, not rendered here — see the component note.
   * Still required, and still the page's own words: it is what the `<h1>` will say.
   */
  title: string;
  onBack?: () => void;
  backLabel?: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Back navigation and page actions for a detail or create page.
 *
 * This component USED to render the page's `<h1>`. Aaron reversed heading ownership on
 * 2026-08-18 — one title per screen, in the chrome — so it now sends `title` up to the
 * chrome via `usePageTitleOverride` and renders only the back control and the actions.
 *
 * Publishing rather than deleting the prop is what makes the sweep non-lossy. MEASURED
 * across the 33 call sites: 10 pass a title the URL does not contain — `campaign.name`,
 * `profile.supplierName`, `contact.name`, `file.fileName`,
 * `profile.full_name ?? profile.email` — and the route table in `page-titles.ts` can only
 * ever answer those with a static label like "Vendor Details". Dropping the prop would
 * have quietly downgraded all ten headings from the record to the category.
 *
 * It renders nothing when there is no back control and no actions, because an empty flex
 * row still consumes a `gap` of page margin below the chrome. One call site is in that
 * shape today (`admin/CanSpamReadiness.tsx`); the rest carry a back button, actions, or
 * both.
 *
 * THE ROW SURVIVES ITS OWN CHILDREN. This is the part that had to be fixed, and the
 * cause was one declaration, not the obvious one.
 *
 * The left block used to be `flex: 1 1 0%` WITH `minWidth: 0`, and the actions block
 * `flexShrink: 0`, in a `nowrap` row. A flex item's default `min-width: auto` is the
 * browser's own guarantee that a control cannot be shrunk below its content;
 * `minWidth: 0` deliberately switches that guarantee off. So the row's first response to
 * pressure was to crush the ONE control that gets a user out of the page, while the
 * actions — which could not shrink — ran off both edges of the viewport.
 *
 * MEASURED at 390x664 (iPhone 13, coarse pointer, `--salt-size-base` 44px), adding one
 * more command to each header pushed it clean off the screen on every call site that
 * carries actions: CampaignDetail 108px past the right edge, CampaignAddSuppliers 123px,
 * EmailStudio 188px, ComplianceCenter 47px, CorporateContactDetail 55px. Nothing was
 * scrollable and nothing wrapped, so those commands were unreachable, not merely ugly.
 *
 * Three changes, each load-bearing:
 *
 *  1. `FlexLayout`, not `StackLayout`. `wrap` and `justify` are FlexLayout props;
 *     `StackLayoutProps` does not declare either (`@salt-ds/core` StackLayout.d.ts) and
 *     StackLayout hard-writes `--flexLayout-wrap: nowrap`. Reaching for the behaviour
 *     through an inline `flexWrap` on the wrong component is how a layout prop ends up
 *     fighting the component that owns it.
 *  2. The back block is `flex: 0 0 auto` with NO `minWidth: 0`. It is one icon button;
 *     there is nothing in it to reflow, so it should never be the row's give. Removing
 *     `minWidth: 0` restores the browser's min-content floor — the back control cannot
 *     reach zero width again even if this file is edited carelessly later.
 *  3. The actions block grows (`flex: 1 1 auto`), keeps `minWidth: 0` so it is the part
 *     that yields, and wraps. `justify="end"` holds the actions right-aligned on the
 *     first line and on any line they wrap onto.
 *
 * The row wraps rather than scrolls on purpose: a horizontally scrolled command row gives
 * no indication that anything is off to the right, which is the same unreachability in a
 * quieter costume.
 */
export function PageHeader({ title, onBack, backLabel = "Back", className, children }: PageHeaderProps) {
  usePageTitleOverride(title);

  if (!onBack && !children) return null;

  return (
    <FlexLayout align="center" className={className} direction="row" gap={2} wrap>
      {onBack && (
        <FlexLayout align="center" direction="row" gap={2} style={{ flex: "0 0 auto" }}>
          <IconActionButton
            icon={SaltIcons.ArrowLeftIcon}
            label={backLabel}
            onClick={onBack}
            variant="outline"
          />
        </FlexLayout>
      )}

      {children && (
        <FlexLayout
          align="center"
          direction="row"
          gap={1}
          justify="end"
          style={{ flex: "1 1 auto", minWidth: 0 }}
          wrap
        >
          {children}
        </FlexLayout>
      )}
    </FlexLayout>
  );
}
